/**
 * Tiny RxJS-compatible facade for legacy callers.
 *
 * The legacy shell exposes several globals that return `Observable<T>`
 * (e.g. `EY_RAPToR_getAccessTokenforMFE`, `EY_RAPToR_URLClientChange`).
 * We don't want to ship `rxjs` in the React shell, but legacy consumers
 * call `.subscribe(next, error, complete)` on the result.
 *
 * This shim provides just enough of the `Observable` surface to satisfy
 * those callers: `subscribe(observerOrNext)`, returning a `Subscription`
 * with `unsubscribe()`. It also implements the `[Symbol.observable]`
 * interop key so libraries that do `from(value)` keep working.
 *
 * If a future MFE needs richer operators it should adopt the SDK
 * `shell.auth.getToken(...)` Promise API directly (post-deprecation).
 */

export interface Subscription {
  unsubscribe(): void;
}

interface Observer<T> {
  next?(value: T): void;
  error?(err: unknown): void;
  complete?(): void;
}

/**
 * RxJS-style operator: takes a source Observable and returns a new one.
 * Legacy MFEs import operators from their own bundled `rxjs/operators`,
 * so we never construct them — we just chain whatever they pass into `.pipe`.
 */
type OperatorFunction<T, R> = (source: MinimalObservable<T>) => MinimalObservable<R>;

interface MinimalObservable<T> {
  subscribe(
    observerOrNext: Observer<T> | ((value: T) => void),
    error?: (err: unknown) => void,
    complete?: () => void,
  ): Subscription;
  /**
   * RxJS-compatible pipe. Legacy callers in MFEs (e.g. myteams ngrx effects)
   * do `EY_RAPToR_getRumToken(...).pipe(map(...), catchError(...))`. Each
   * operator is `(source) => Observable`; we reduce them over the source.
   * The operators come from the MFE's own bundled RxJS, so the chain ends
   * up as a real RxJS Observable after the first operator runs.
   */
  pipe<R>(...operators: OperatorFunction<unknown, unknown>[]): MinimalObservable<R>;
}

function makePipe<T>(self: MinimalObservable<T>): MinimalObservable<T>['pipe'] {
  return function pipe<R>(
    ...operators: OperatorFunction<unknown, unknown>[]
  ): MinimalObservable<R> {
    return operators.reduce<MinimalObservable<unknown>>(
      (src, op) => op(src),
      self as MinimalObservable<unknown>,
    ) as MinimalObservable<R>;
  };
}

function normalize<T>(
  observerOrNext: Observer<T> | ((value: T) => void),
  error?: (err: unknown) => void,
  complete?: () => void,
): Required<Observer<T>> {
  if (typeof observerOrNext === 'function') {
    return {
      next: observerOrNext,
      error: error ?? (() => undefined),
      complete: complete ?? (() => undefined),
    };
  }
  return {
    next: observerOrNext.next ?? (() => undefined),
    error: observerOrNext.error ?? (() => undefined),
    complete: observerOrNext.complete ?? (() => undefined),
  };
}

/** Cold observable that emits a single synchronous value. */
export function observableOf<T>(value: T): MinimalObservable<T> {
  const obs: MinimalObservable<T> = {
    subscribe(observerOrNext, error, complete) {
      let cancelled = false;
      const o = normalize(observerOrNext, error, complete);
      // Emit synchronously to match legacy `of(value)` semantics.
      if (!cancelled) {
        try {
          o.next(value);
          o.complete();
        } catch (err) {
          o.error(err);
        }
      }
      return {
        unsubscribe() {
          cancelled = true;
        },
      };
    },
    pipe: undefined as unknown as MinimalObservable<T>['pipe'],
  };
  obs.pipe = makePipe(obs);
  return obs;
}

/** Cold observable that bridges a Promise — emits once on resolve, or errors. */
export function observableFromPromise<T>(promise: Promise<T>): MinimalObservable<T> {
  const obs: MinimalObservable<T> = {
    subscribe(observerOrNext, error, complete) {
      let cancelled = false;
      const o = normalize(observerOrNext, error, complete);
      promise
        .then((value) => {
          if (cancelled) return;
          o.next(value);
          o.complete();
        })
        .catch((err) => {
          if (cancelled) return;
          o.error(err);
        });
      return {
        unsubscribe() {
          cancelled = true;
        },
      };
    },
    pipe: undefined as unknown as MinimalObservable<T>['pipe'],
  };
  obs.pipe = makePipe(obs);
  return obs;
}

/**
 * Minimal multicast Subject — a hot observable the shell can push into and
 * legacy callers `.subscribe()` to. Used by the SignalR bridge to expose
 * `EY_RAPToR_portalSignalRObservable`, which MFEs subscribe to for real-time
 * push messages (the legacy `SignalRService.portalMessageObservable`).
 *
 * Unlike `observableOf`, a Subject never completes on subscribe — it stays
 * open and re-emits every `next(value)` to all current subscribers.
 */
export interface SubjectLike<T> {
  readonly observable: MinimalObservable<T>;
  next(value: T): void;
  error(err: unknown): void;
  complete(): void;
}

export function createSubject<T>(): SubjectLike<T> {
  const observers = new Set<Required<Observer<T>>>();
  let closed = false;

  const observable: MinimalObservable<T> = {
    subscribe(observerOrNext, error, complete) {
      const o = normalize(observerOrNext, error, complete);
      if (closed) {
        // Match RxJS: subscribing to a completed Subject completes immediately.
        o.complete();
        return { unsubscribe() {} };
      }
      observers.add(o);
      return {
        unsubscribe() {
          observers.delete(o);
        },
      };
    },
    pipe: undefined as unknown as MinimalObservable<T>['pipe'],
  };
  observable.pipe = makePipe(observable);

  return {
    observable,
    next(value) {
      if (closed) return;
      for (const o of [...observers]) {
        try {
          o.next(value);
        } catch (err) {
          o.error(err);
        }
      }
    },
    error(err) {
      if (closed) return;
      closed = true;
      for (const o of [...observers]) o.error(err);
      observers.clear();
    },
    complete() {
      if (closed) return;
      closed = true;
      for (const o of [...observers]) o.complete();
      observers.clear();
    },
  };
}
